import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Check,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  fetchArsenal,
  fetchArsenalCatalog,
  addArsenalItem,
  updateArsenalItem,
  deleteArsenalItem,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * BeautyArsenalCard — Settings card where returning users log products
 * they already own. The /analyze endpoint pulls this list server-side
 * and tells the LLM to weave owned products into routine `tip` fields
 * rather than always recommending new purchases.
 *
 * Lightweight inline add/edit form (no modal) so the friction of logging
 * a new product is as low as possible — users have full hands and tired
 * shoulders when they finish a routine. Keep it one tap.
 */
const EMPTY = { brand: "", name: "", category: "", shade: "", notes: "" };

const ArsenalForm = ({ initial, categories, onSave, onCancel, busy }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial || EMPTY);
  const update = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const canSave =
    draft.brand.trim().length > 0 &&
    draft.name.trim().length > 0 &&
    draft.category.trim().length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25 }}
      className="overflow-hidden"
    >
      <div
        className="grid sm:grid-cols-2 gap-3 mt-3 p-4 rounded-2xl bg-[#FAF5F1] border border-[#EADDD7]"
        data-testid="arsenal-form"
      >
        <div className="sm:col-span-1">
          <label className="block text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
            {t("arsenal.form.brandLabel")}
          </label>
          <input
            type="text"
            value={draft.brand}
            onChange={update("brand")}
            placeholder={t("arsenal.form.brandPlaceholder")}
            maxLength={80}
            className="w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm"
            data-testid="arsenal-form-brand"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
            {t("arsenal.form.nameLabel")}
          </label>
          <input
            type="text"
            value={draft.name}
            onChange={update("name")}
            placeholder={t("arsenal.form.namePlaceholder")}
            maxLength={120}
            className="w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm"
            data-testid="arsenal-form-name"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
            {t("arsenal.form.categoryLabel")}
          </label>
          <select
            value={draft.category}
            onChange={update("category")}
            className="w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm"
            data-testid="arsenal-form-category"
          >
            <option value="">{t("arsenal.form.categoryPlaceholder")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
            {t("arsenal.form.shadeLabel")}{" "}
            <span className="lowercase italic">
              {t("arsenal.form.shadeOptional")}
            </span>
          </label>
          <input
            type="text"
            value={draft.shade}
            onChange={update("shade")}
            placeholder={t("arsenal.form.shadePlaceholder")}
            maxLength={60}
            className="w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm"
            data-testid="arsenal-form-shade"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
            {t("arsenal.form.notesLabel")}{" "}
            <span className="lowercase italic">
              {t("arsenal.form.shadeOptional")}
            </span>
          </label>
          <input
            type="text"
            value={draft.notes}
            onChange={update("notes")}
            placeholder={t("arsenal.form.notesPlaceholder")}
            maxLength={240}
            className="w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm"
            data-testid="arsenal-form-notes"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-full border border-[#EADDD7] text-[#5C4B51] hover:bg-white text-sm disabled:opacity-50"
            data-testid="arsenal-form-cancel"
          >
            {t("arsenal.form.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            disabled={!canSave || busy}
            className="px-5 py-2 rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white text-sm disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="arsenal-form-save"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("arsenal.form.save")}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const BeautyArsenalCard = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maxItems, setMaxItems] = useState(50);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalog, mine] = await Promise.all([
          fetchArsenalCatalog(),
          fetchArsenal(),
        ]);
        if (cancelled) return;
        setCategories(catalog?.categories || []);
        setMaxItems(catalog?.max_items || 50);
        setItems(mine?.items || []);
        track("arsenal_opened", { item_count: (mine?.items || []).length });
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryLabel = (id) =>
    categories.find((c) => c.id === id)?.label || id;

  const handleAdd = async (draft) => {
    setBusy(true);
    try {
      const res = await addArsenalItem({
        brand: draft.brand.trim(),
        name: draft.name.trim(),
        category: draft.category,
        shade: draft.shade?.trim() || null,
        notes: draft.notes?.trim() || null,
      });
      setItems(res.items || []);
      setAdding(false);
      track("arsenal_item_added", {
        category: draft.category,
        has_shade: !!draft.shade?.trim(),
      });
      toast.success(t("arsenal.toastAdded", { brand: draft.brand, name: draft.name }));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("arsenal.toastAddFail"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (id, draft) => {
    setBusy(true);
    try {
      const res = await updateArsenalItem(id, {
        brand: draft.brand.trim(),
        name: draft.name.trim(),
        category: draft.category,
        shade: draft.shade?.trim() || null,
        notes: draft.notes?.trim() || null,
      });
      setItems(res.items || []);
      setEditingId(null);
      track("arsenal_item_edited", { category: draft.category });
      toast.success(t("arsenal.toastSaved"));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("arsenal.toastSaveFail"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id, brand, name) => {
    const ok = window.confirm(
      t("arsenal.confirmDelete", { brand, name }),
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await deleteArsenalItem(id);
      setItems(res.items || []);
      track("arsenal_item_removed");
      toast.success(t("arsenal.toastRemoved"));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("arsenal.toastRemoveFail"),
      );
    } finally {
      setBusy(false);
    }
  };

  // Group items by category for visual grouping (most useful for users
  // with full kits — primer/foundation/concealer cluster together).
  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  const orderedCategories = categories
    .map((c) => c.id)
    .filter((id) => grouped[id]?.length);

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 sm:p-7 soft-shadow mb-5"
      data-testid="beauty-arsenal-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0">
            <Briefcase className="h-5 w-5 text-[#9B6A6C]" />
          </div>
          <div>
            <h3
              className="text-xl font-medium text-[#2C2123]"
              data-testid="arsenal-heading"
            >
              {t("arsenal.heading")}
            </h3>
            <p className="text-sm text-[#5C4B51] leading-relaxed mt-1 max-w-md">
              {t("arsenal.intro")}
            </p>
          </div>
        </div>
        {!loading && items.length < maxItems && !adding && (
          <button
            type="button"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
            className="rounded-full bg-[#9B6A6C] hover:bg-[#7d5455] text-white px-4 py-2 text-sm inline-flex items-center gap-1.5 transition-all shrink-0"
            data-testid="arsenal-add-btn"
          >
            <Plus className="h-4 w-4" />
            {t("arsenal.addBtn")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A7D82] py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("arsenal.loading")}
        </div>
      ) : (
        <>
          {items.filter((it) => it.source === "trouve_chat").length > 0 && (
            <div
              className="mb-4 inline-flex items-center gap-2 text-xs text-[#9B6A6C] bg-gradient-to-r from-[#FBE7EE] to-[#FAF5F1] border border-[#D5A8A4]/50 rounded-full px-3 py-1.5"
              data-testid="arsenal-trouve-stat"
            >
              <Sparkles className="h-3 w-3" />
              {t("arsenal.trouvePoweredStat", {
                count: items.filter((it) => it.source === "trouve_chat").length,
              })}
            </div>
          )}
          <AnimatePresence>
            {adding && (
              <ArsenalForm
                key="add"
                initial={EMPTY}
                categories={categories}
                busy={busy}
                onCancel={() => setAdding(false)}
                onSave={handleAdd}
              />
            )}
          </AnimatePresence>

          {items.length === 0 && !adding && (
            <div
              className="rounded-2xl bg-[#FAF5F1] border border-dashed border-[#EADDD7] p-6 text-center"
              data-testid="arsenal-empty"
            >
              <p className="text-sm text-[#5C4B51] mb-3">
                {t("arsenal.emptyBody")}
              </p>
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="text-sm text-[#9B6A6C] hover:text-[#7d5455] font-medium underline underline-offset-2"
                data-testid="arsenal-empty-add-btn"
              >
                {t("arsenal.emptyAddBtn")}
              </button>
            </div>
          )}

          {items.length > 0 && (
            <div
              className="space-y-5 mt-3"
              data-testid="arsenal-items"
            >
              {orderedCategories.map((catId) => (
                <div key={catId}>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#9B6A6C] mb-2 font-medium">
                    {categoryLabel(catId)}
                  </div>
                  <div className="space-y-2">
                    {grouped[catId].map((it) =>
                      editingId === it.id ? (
                        <ArsenalForm
                          key={it.id}
                          initial={{
                            brand: it.brand,
                            name: it.name,
                            category: it.category,
                            shade: it.shade || "",
                            notes: it.notes || "",
                          }}
                          categories={categories}
                          busy={busy}
                          onCancel={() => setEditingId(null)}
                          onSave={(d) => handleUpdate(it.id, d)}
                        />
                      ) : (
                        <div
                          key={it.id}
                          className={`flex items-start justify-between gap-3 p-3 rounded-xl border transition-all ${
                            it.source === "trouve_chat"
                              ? "bg-gradient-to-br from-[#FBE7EE] to-[#FAF5F1] border-[#D5A8A4]/60 hover:border-[#9B6A6C]"
                              : "bg-[#FAF5F1] border-[#EADDD7] hover:border-[#D5A8A4]"
                          }`}
                          data-testid={`arsenal-item-${it.id}`}
                          data-source={it.source || "manual"}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#2C2123] truncate">
                              {it.brand}{" "}
                              <span className="font-normal text-[#5C4B51]">
                                {it.name}
                              </span>
                            </div>
                            {it.source === "trouve_chat" && (
                              <div
                                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-[#9B6A6C] bg-white/70 px-1.5 py-0.5 rounded-full border border-[#D5A8A4]/50"
                                data-testid={`arsenal-item-${it.id}-trouve-badge`}
                              >
                                <Sparkles className="h-2.5 w-2.5" />
                                {t("arsenal.trouveSavedBadge")}
                              </div>
                            )}
                            {(it.shade || (it.notes && it.source !== "trouve_chat")) && (
                              <div className="text-xs text-[#8A7D82] mt-0.5 truncate">
                                {it.shade && (
                                  <span className="text-[#9B6A6C]">
                                    {it.shade}
                                  </span>
                                )}
                                {it.shade && it.notes && it.source !== "trouve_chat" && (
                                  <span> · </span>
                                )}
                                {it.notes && it.source !== "trouve_chat" && <span>{it.notes}</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(it.id);
                                setAdding(false);
                              }}
                              className="p-1.5 rounded-full hover:bg-white text-[#8A7D82] hover:text-[#9B6A6C] transition-colors"
                              data-testid={`arsenal-edit-${it.id}`}
                              aria-label={t("arsenal.editAria", { brand: it.brand, name: it.name })}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDelete(it.id, it.brand, it.name)
                              }
                              className="p-1.5 rounded-full hover:bg-white text-[#8A7D82] hover:text-[#9B6A6C] transition-colors"
                              data-testid={`arsenal-delete-${it.id}`}
                              aria-label={t("arsenal.removeAria", { brand: it.brand, name: it.name })}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {items.length >= maxItems && (
            <p className="text-xs text-[#8A7D82] mt-3 text-center">
              {t("arsenal.fullNote", { count: maxItems })}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default BeautyArsenalCard;
